import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import Tabs, { type TabItem } from '@/components/base/Tabs';
import Toast from '@/components/base/Toast';
import { formatCurrency } from '@/lib/format';
import { systemSettings } from '@/mocks/settings';
import {
  fetchOutstanding,
  fetchRevenue,
  fetchSettlements,
  type FinancePeriod,
  type RevenueResult,
} from '@/services/financeService';
import type { OutstandingDues, SettlementRequest } from '@/types';
import CommissionsTable from './components/CommissionsTable';
import OutstandingSection from './components/OutstandingSection';
import PeriodSelector from './components/PeriodSelector';
import RequestSettlementModal from './components/RequestSettlementModal';
import RevenueCards from './components/RevenueCards';
import SettlementsTable from './components/SettlementsTable';

type FinanceTab = 'revenue' | 'dues' | 'settlements';

const TAB_IDS: FinanceTab[] = ['revenue', 'dues', 'settlements'];

interface ToastState {
  message: string;
  tone: 'success' | 'error';
}

export default function FinancePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState<FinanceTab>(
    requestedTab && TAB_IDS.includes(requestedTab as FinanceTab) ? (requestedTab as FinanceTab) : 'revenue',
  );
  const [period, setPeriod] = useState<FinancePeriod>('today');

  // Revenue + commission details (depends on the selected period).
  const [revenue, setRevenue] = useState<RevenueResult | null>(null);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState(false);

  const loadRevenue = useCallback(async () => {
    setRevenueLoading(true);
    setRevenueError(false);
    try {
      setRevenue(await fetchRevenue(period));
    } catch {
      setRevenueError(true);
    } finally {
      setRevenueLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadRevenue();
  }, [loadRevenue]);

  // Outstanding dues (independent from the period).
  const [outstanding, setOutstanding] = useState<OutstandingDues | null>(null);
  const [outstandingLoading, setOutstandingLoading] = useState(true);
  const [outstandingError, setOutstandingError] = useState(false);

  const loadOutstanding = useCallback(async () => {
    setOutstandingLoading(true);
    setOutstandingError(false);
    try {
      setOutstanding(await fetchOutstanding());
    } catch {
      setOutstandingError(true);
    } finally {
      setOutstandingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOutstanding();
  }, [loadOutstanding]);

  // Settlement log.
  const [settlements, setSettlements] = useState<SettlementRequest[] | null>(null);
  const [settlementsLoading, setSettlementsLoading] = useState(true);
  const [settlementsError, setSettlementsError] = useState(false);

  const loadSettlements = useCallback(async () => {
    setSettlementsLoading(true);
    setSettlementsError(false);
    try {
      setSettlements(await fetchSettlements());
    } catch {
      setSettlementsError(true);
    } finally {
      setSettlementsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettlements();
  }, [loadSettlements]);

  const [requestOpen, setRequestOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const handleRequested = (settlement: SettlementRequest) => {
    setSettlements((prev) => (prev ? [settlement, ...prev] : [settlement]));
    setRequestOpen(false);
    setToast({ message: t('finance.req.toast'), tone: 'success' });
    // Re-read balances from the system result instead of assuming a change.
    loadOutstanding();
  };

  const tabs: TabItem[] = TAB_IDS.map((id) => ({ id, label: t(`finance.tab.${id}`) }));
  const commissionRows = revenue?.details ?? [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('finance.title')}</h1>
          <p className="mt-1 text-sm text-foreground-500">{t('finance.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {outstanding ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs text-foreground-600">
              <i className="ri-wallet-3-line text-base text-foreground-400" aria-hidden="true" />
              <span className="whitespace-nowrap">
                {t('finance.outstanding.balanceLabel')}:{' '}
                <span className="font-semibold text-foreground-900">
                  {formatCurrency(outstanding.total, outstanding.currency)}
                </span>
              </span>
            </span>
          ) : null}
          <Button variant="primary" size="md" icon="ri-send-plane-line" onClick={() => setRequestOpen(true)}>
            {t('finance.requestSettlement')}
          </Button>
        </div>
      </header>

      <Card padded={false} className="overflow-hidden">
        <div className="border-b border-background-200 px-4 py-3.5 md:px-5">
          <Tabs items={tabs} active={tab} onChange={(id) => setTab(id as FinanceTab)} />
        </div>

        {tab === 'revenue' ? (
          <div className="space-y-5 p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-foreground-950">{t('finance.tab.revenue')}</h2>
                <p className="mt-0.5 text-xs text-foreground-500">{t('finance.period.label')}</p>
              </div>
              <PeriodSelector value={period} onChange={setPeriod} />
            </div>

            {revenueError ? (
              <ErrorState
                title={t('finance.details.error')}
                description={t('common.errorText')}
                retryLabel={t('common.retry')}
                onRetry={loadRevenue}
              />
            ) : (
              <>
                <RevenueCards summary={revenue?.summary ?? null} loading={revenueLoading} />

                <div className="rounded-lg border border-background-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 px-4 py-3.5">
                    <h3 className="text-sm font-semibold text-foreground-950">
                      {t('finance.details.title')}
                    </h3>
                  </div>

                  {revenueLoading ? (
                    <div className="space-y-3 p-4">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-4">
                          <Skeleton className="h-5 w-20" />
                          <Skeleton className="h-5 flex-1" />
                          <Skeleton className="h-5 w-28" />
                        </div>
                      ))}
                    </div>
                  ) : commissionRows.length === 0 ? (
                    <div className="p-4">
                      <EmptyState
                        icon="ri-wallet-3-line"
                        title={t('finance.details.empty')}
                        description={t('finance.details.emptyDesc')}
                      />
                    </div>
                  ) : (
                    <CommissionsTable details={commissionRows} currency={systemSettings.currency} />
                  )}
                </div>

                <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                  <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
                  {t('finance.details.note')}
                </p>
              </>
            )}
          </div>
        ) : null}

        {tab === 'dues' ? (
          <div className="p-4 md:p-5">
            <OutstandingSection
              data={outstanding}
              loading={outstandingLoading}
              error={outstandingError}
              onRetry={loadOutstanding}
              onRequest={() => setRequestOpen(true)}
            />
          </div>
        ) : null}

        {tab === 'settlements' ? (
          <div>
            <div className="flex items-center justify-between gap-2 border-b border-background-200 px-4 py-3.5 md:px-5">
              <h2 className="text-base font-semibold text-foreground-950">{t('finance.set.title')}</h2>
            </div>

            {settlementsLoading ? (
              <div className="space-y-3 p-4 md:p-5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 flex-1" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                ))}
              </div>
            ) : settlementsError ? (
              <div className="p-4 md:p-5">
                <ErrorState
                  title={t('finance.set.error')}
                  description={t('common.errorText')}
                  retryLabel={t('common.retry')}
                  onRetry={loadSettlements}
                />
              </div>
            ) : !settlements || settlements.length === 0 ? (
              <div className="p-4 md:p-5">
                <EmptyState
                  icon="ri-exchange-dollar-line"
                  title={t('finance.set.empty')}
                  description={t('finance.set.emptyDesc')}
                />
              </div>
            ) : (
              <SettlementsTable settlements={settlements} currency={systemSettings.currency} />
            )}
          </div>
        ) : null}
      </Card>

      <p className="flex items-start gap-2 px-1 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
        {t('finance.demoNote')}
      </p>

      <RequestSettlementModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSuccess={handleRequested}
      />

      {toast ? <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /> : null}
    </div>
  );
}